import React from 'react';
import { CSSObject } from '@emotion/react';

import {
  AdditionalComponent,
  FeedbackComponent,
  UiComponent,
} from 'components';
import { tableWrap } from 'styles/table';
import { detailWrap } from 'styles/table';
import UserMemoModal from 'components/user/UserMemoModal';
import useVolunteerList from './useVolunteerList';
import useUserMemo from './useUserMemo';
import useEnrollResponse from './useEnrollResponse';
import { useParams } from 'react-router-dom';
import useModal from 'hooks/useModal';
import NotificationModal from './NotificationModal';
import VolunteerAddModal from './VolunteerAddModal';
import DownloadModal from './DownloadModal';
import FeedbackModal from './FeedbackModal';
import AttendanceStatusModal from './AttendanceStatusModal';
import VolunteerDeleteModal from './VolunteerDeleteModal';
import { toast } from 'react-toastify';
import VolunteerActionMenu, {
  VolunteerActionMenuItem,
} from './VolunteerActionMenu';
import SelectVolunteerModal from './SelectVolunteerModal';
import VolunteerFilter from './VolunteerFilter';
import useDetail from '../useDetail';

/** 타이틀 좌측 / 더보기 메뉴 우측 — 봉사 정보 헤더와 같은 배치 */
const header: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  h3: { margin: 0 },
};

/** 필터는 헤더 아래 좌측에 붙인다 */
const toolbar: CSSObject = {
  marginTop: '16px',
};

function VolunteerList() {
  const { id } = useParams();
  const { handleModal, handleClose } = useModal();
  const { event } = useDetail();

  const {
    isOn: isOnUserMemo,
    handleOpen: hanldeOpenUserMemo,
    handleClose: handleCloseUserMemo,
    selectedUserId,
  } = useUserMemo();

  const {
    isOn: isOnAdditional,
    handleOpen: handleOpenAdditional,
    handleClose: handleCloseAdditional,
    selectedData: selectedAdditionalData,
  } = useEnrollResponse();

  const {
    isOn: isOnFeedback,
    handleOpen: handleOpenFeedback,
    handleClose: handleCloseFeedback,
    selectedData: selectedFeedbackData,
  } = useEnrollResponse();

  const {
    data,
    pageState,
    cols,
    renderer,
    handleChangeStatus,
    handleRemove,
    deletableEnrolls,
    excludedDeleteCount,
    isRemoving,
    handlePostFeedbackNotification,
    isLottery,
    selectedCount,
    checkedEnrolls,
    isChangingStatus,
    hasSelected,
    generalEnrollCount,
    handleConfirmSelection,
    handleSelectAdditional,
    isSelectionPending,
    nameKeyword,
    handleSearchName,
    handleChangeAttendanceFilter,
    handleChangeSelectionFilter,
  } = useVolunteerList({
    hanldeOpenUserMemo,
    handleOpenAdditional,
    handleOpenFeedback,
  });

  const selectionLabel = hasSelected ? '추가 참여 확정' : '참여 확정';

  const eventStatus = event?.eventStatus;
  const isCompleted = eventStatus === 'COMPLETED';
  const isRecruitmentClosed = eventStatus === 'RECRUITMENT_CLOSED';

  const actionItems: VolunteerActionMenuItem[] = [
    {
      // 대상이 없으면 열어도 할 게 없어 아예 감춘다
      label: '출결상태 변경',
      visible: isRecruitmentClosed && selectedCount > 0,
      onClick: () =>
        handleModal(
          '출결상태 변경',
          <AttendanceStatusModal
            targets={checkedEnrolls}
            isPending={isChangingStatus}
            onSubmit={(status) => {
              handleChangeStatus(status);
              handleClose();
            }}
          />,
          <></>,
        ),
    },
    {
      // 추첨형 확정 — 선정자가 생기면 '추가 참여 확정'으로 바뀐다.
      // 후보는 확정 전이면 신청 완료자, 확정 후면 아직 확정되지 않은 사람 전부다.
      // (확정 뒤에도 추가 모집분과 취소 후 재신청분이 APPLIED 로 들어온다)
      label: selectionLabel,
      visible: isLottery && (isRecruitmentClosed || isCompleted),
      disabled: isSelectionPending,
      onClick: () =>
        handleModal(
          selectionLabel,
          <SelectVolunteerModal
            eventId={Number(id)}
            candidateStatus={hasSelected ? 'NOT_SELECTED,APPLIED' : 'APPLIED'}
            total={generalEnrollCount}
            isPending={isSelectionPending}
            submitTitle={selectionLabel}
            onSubmit={(enrollIds) => {
              if (hasSelected) handleSelectAdditional(enrollIds);
              else handleConfirmSelection(enrollIds);
              handleClose();
            }}
          />,
          <></>,
        ),
    },
    {
      label: '참석자 추가',
      visible: !isCompleted,
      onClick: () => handleModal('참석자 추가', <VolunteerAddModal />, <></>),
    },
    {
      label: '참석자 삭제',
      visible: !isCompleted && deletableEnrolls.length > 0,
      disabled: isRemoving,
      onClick: () => {
        // 일부만 조용히 빠지면 위험해서, 섞여 있으면 아예 열지 않는다
        if (excludedDeleteCount > 0) {
          toast.error(
            '직접 신청한 참석자는 삭제할 수 없습니다. 어드민이 추가한 참석자만 선택해주세요.',
          );
          return;
        }

        handleModal(
          '참석자 삭제',
          <VolunteerDeleteModal
            targets={deletableEnrolls}
            isPending={isRemoving}
            onSubmit={() => {
              handleRemove();
              handleClose();
            }}
          />,
          <></>,
        );
      },
    },
    {
      // 모집중 이후 ~ 진행완료 전
      label: '알림 전송',
      visible:
        eventStatus === 'RECRUITING' || isRecruitmentClosed || isCompleted,
      onClick: () => handleModal('알림 전송', <NotificationModal />, <></>),
    },
    {
      label: '피드백 알림 전송',
      visible: isRecruitmentClosed || isCompleted,
      onClick: handlePostFeedbackNotification,
    },
    {
      label: '피드백',
      visible: isCompleted,
      onClick: () => handleModal('피드백', <FeedbackModal />, <></>),
    },
    {
      label: '엑셀다운로드',
      visible: true,
      onClick: () => handleModal('엑셀다운로드', <DownloadModal />, <></>),
    },
  ];

  return (
    <>
      <div css={[detailWrap, { marginTop: '24px' }]}>
        <div css={header}>
          <h3>참석자 정보</h3>
          <div css={{ display: 'flex', alignItems: 'center', gap: '0 8px' }}>
            {selectedCount > 0 && (
              <span css={{ fontSize: '13px', color: '#5f6069' }}>
                선택 {selectedCount}명
              </span>
            )}
            <VolunteerActionMenu items={actionItems} />
          </div>
        </div>

        <div css={toolbar}>
          <VolunteerFilter
            nameKeyword={nameKeyword}
            showAttendance={isRecruitmentClosed || isCompleted}
            showSelection={isLottery}
            onSearchName={handleSearchName}
            onChangeAttendance={handleChangeAttendanceFilter}
            onChangeSelection={handleChangeSelectionFilter}
          />
        </div>

        <div css={tableWrap}>
          <UiComponent.Table
            cols={cols}
            renderer={renderer}
            data={data?.data || []}
            sticky
          />
        </div>
        <UiComponent.Pagination
          {...pageState}
          totalPage={data?.totalPages || 1}
        />
      </div>
      {isOnUserMemo && (
        <UserMemoModal
          id={selectedUserId || -1}
          onClose={handleCloseUserMemo}
        />
      )}
      {isOnAdditional && (
        <AdditionalComponent.ViewModal
          eventId={id || ''}
          data={selectedAdditionalData}
          onClose={handleCloseAdditional}
        />
      )}
      {isOnFeedback && (
        <FeedbackComponent.ViewModal
          eventId={id || ''}
          data={selectedFeedbackData}
          onClose={handleCloseFeedback}
        />
      )}
    </>
  );
}

export default VolunteerList;
